import React, { useEffect, useContext, useState, useRef } from 'react'
import gsap from 'gsap'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { StoreState } from '../../Store'

// Components
import Grid from '../Grid'
import GridItem from '../GridItem'
import AnimateScaleIn from '../animation/AnimateScaleIn'

// Hooks
import { useScrolledAnimation } from '../animation/useScrolledAnimation'

// Functions
import { countryCookieCheck } from '../../utils/utils'

// Vars
import { Heading4 } from '../../styles/vars/textStyles.style'

// SVGs
import Info from '../svgs/Info'

// Styles
import {
  ItineraryItem,
  ItineraryImage,
  ItineraryIcon,
  ItineraryDetails,
  ItineraryLocation,
  ItineraryDate,
  ItineraryBadge,
  ItineraryLink,
  ItineraryItemLine,
} from './index.style'

// Image
import WavesImage from './assets/waves.png'

const Item = ({
  id,
  contentful_id,
  delay,
  internal,
  image,
  location,
  wonderIcon,
  link,
  date1,
  dateOfCruisingDay,
  translations,
}) => {
  const store = useContext(StoreState)

  const [linkSuffix, setLinkSuffix] = useState('')

  useEffect(() => {
    if (store.countryCookie !== null) {
      const suffix = countryCookieCheck(store.countryCookie)
      setLinkSuffix(suffix)
    }
  }, [store.countryCookie])

  const labelRef = useRef(null),
    lineRef = useRef(null),
    dateRef = useRef(null),
    itemIn = useRef(null)

  useScrolledAnimation({
    id: contentful_id,
    transition: itemIn,
  })

  useEffect(() => {
    if (!labelRef.current || !lineRef.current || !dateRef.current) return

    itemIn.current = gsap.timeline({
      paused: true,
      defaults: {
        duration: 1.5,
        ease: 'power2.out',
      },
    })

    itemIn.current.fromTo(
      labelRef.current,
      {
        opacity: 0,
        x: 50,
      },
      {
        opacity: 1,
        x: 0,
      },
      0
    )

    itemIn.current.fromTo(
      lineRef.current,
      {
        opacity: 1,
        scaleX: 0,
      },
      {
        opacity: 0.2,
        scaleX: 1,
        ease: 'power2.inOut',
      },
      0
    )

    itemIn.current.fromTo(
      dateRef.current,
      {
        opacity: 0,
      },
      {
        opacity: 1,
      },
      0
    )
  }, [labelRef, lineRef, dateRef])

  if (internal.type === 'ContentfulItemItineraryItem') {
    return (
      <ItineraryItem delay={delay} data-scroll data-scroll-id={contentful_id}>
        <Grid>
          <GridItem tiny={3} tabletL={2} tabletLAlign={`center`}>
            <ItineraryImage>
              <AnimateScaleIn
                id={contentful_id + '-' + id}
                delay={2}
                scaled={2}
                borderRadius={true}
              >
                <GatsbyImage image={image.gatsbyImageData} alt={location} />
              </AnimateScaleIn>
            </ItineraryImage>
          </GridItem>
          <GridItem tiny={9} tabletL={10}>
            <ItineraryDetails>
              <ItineraryLocation delay={delay} ref={labelRef}>
                <Heading4>
                  {location}
                  {wonderIcon && (
                    <ItineraryBadge
                      bg={wonderIcon.gatsbyImageData.images.fallback.src}
                    />
                  )}
                  {link && (
                    <ItineraryLink
                      href={link.link + linkSuffix}
                      target={'_blank'}
                      wonderIcon={wonderIcon}
                      aria-label={translations.viewCruisePortInformation}
                    >
                      <Info />
                      <Info />
                    </ItineraryLink>
                  )}
                </Heading4>
              </ItineraryLocation>
              <ItineraryDate delay={delay} ref={dateRef}>
                {date1}
              </ItineraryDate>
            </ItineraryDetails>
          </GridItem>
        </Grid>
        <ItineraryItemLine ref={lineRef} />
      </ItineraryItem>
    )
  } else {
    return (
      <ItineraryItem delay={delay} data-scroll data-scroll-id={contentful_id}>
        <Grid>
          <GridItem tiny={3} tinyAlign={`center`} tabletL={2}>
            <ItineraryIcon delay={delay}>
              <img src={WavesImage} alt={`Waves`} height={20} width={98} />
            </ItineraryIcon>
          </GridItem>
          <GridItem tiny={9} tabletL={10}>
            <ItineraryDetails>
              <ItineraryLocation delay={delay} ref={labelRef}>
                {translations.cruisingDay}
              </ItineraryLocation>
              <ItineraryDate delay={delay} ref={dateRef}>
                {dateOfCruisingDay}
              </ItineraryDate>
            </ItineraryDetails>
          </GridItem>
        </Grid>
        <ItineraryItemLine ref={lineRef} />
      </ItineraryItem>
    )
  }
}

Item.propTypes = {
  id: PropTypes.string.isRequired,
  contentful_id: PropTypes.string.isRequired,
  delay: PropTypes.number,
  internal: PropTypes.object,
  image: PropTypes.object,
  location: PropTypes.string,
  wonderIcon: PropTypes.object,
  link: PropTypes.object,
  date1: PropTypes.string,
  dateOfCruisingDay: PropTypes.string,
}

export default Item
